import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useCallback } from 'react';
import { Cloud, CloudOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  count as queueCount, drainQueue, attachAutoDrain,
  countPendingPhotos, listConflicts,
} from '../lib/offlineQueue';
import SyncConflictDialog from './SyncConflictDialog';
import { toast } from 'sonner';

/**
 * Small header pill that shows either:
 *   - offline state (grey CloudOff)
 *   - queued mutation count (amber, clickable → force retry)
 *   - hidden when there's nothing to say (online + empty queue)
 *
 * Reflects the count via the `offline-queue-changed` event that
 * `offlineQueue.enqueue()` / `drainQueue()` emit, plus a lightweight
 * poll every 30s as a safety net.
 */
export default function OfflineSyncBadge() {
  const { t } = useTranslation();
  const [pending, setPending] = useState(0);
  const [photos, setPhotos] = useState(0);
  const [conflicts, setConflicts] = useState(0);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [flushing, setFlushing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [n, p, c] = await Promise.all([
        queueCount(), countPendingPhotos(), listConflicts(),
      ]);
      setPending(n);
      setPhotos(p);
      setConflicts(c.length);
    } catch { /* IndexedDB not available */ }
  }, []);

  useEffect(() => {
    attachAutoDrain();
    refresh();
    const onChanged = () => refresh();
    const onConflicts = (e) => {
      const n = e?.detail?.conflicts || 0;
      if (n > 0) {
        toast.warning(
          `${n} ${n === 1 ? 'item was' : 'items were'} changed by someone else while you were offline — choose which version to keep`,
          { duration: 10000 },
        );
      }
      refresh();
    };
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('offline-queue-changed', onChanged);
    window.addEventListener('offline-sync-conflicts', onConflicts);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const t = setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('offline-queue-changed', onChanged);
      window.removeEventListener('offline-sync-conflicts', onConflicts);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(t);
    };
  }, [refresh]);

  const handleSyncNow = async () => {
    if (flushing) return;
    setFlushing(true);
    try {
      const r = await drainQueue();
      const sent = (r.flushed || 0) + (r.photos || 0);
      if (sent > 0) toast.success(`Synced ${sent} pending ${sent === 1 ? 'item' : 'items'}`);
      if (r.conflicts > 0) {
        toast.warning(`${r.conflicts} ${r.conflicts === 1 ? 'item was' : 'items were'} changed by someone else — choose which version to keep`);
        setConflictOpen(true);
      } else if (r.remaining > 0) {
        toast.warning(`${r.remaining} still pending — will retry`);
      }
    } finally {
      setFlushing(false);
      refresh();
    }
  };

  const total = pending + photos;
  const dialog = (
    <SyncConflictDialog open={conflictOpen} onOpenChange={setConflictOpen} />
  );

  // Conflicts need a decision, so they outrank the plain pending count.
  if (conflicts > 0) {
    return (
      <>
        <button
          type="button"
          onClick={() => setConflictOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 transition hover:bg-red-200"
          data-testid="offline-sync-badge-conflict"
          title={`${conflicts} offline ${conflicts === 1 ? 'change' : 'changes'} clash with newer edits on the server — click to resolve`}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>{conflicts} to resolve</span>
        </button>
        {dialog}
      </>
    );
  }

  if (online && total === 0) return null;

  if (!online) {
    return (
      <>
        <Badge
          variant="secondary"
          className="gap-1.5 border-slate-300 bg-slate-100 text-slate-700"
          data-testid="offline-sync-badge-offline"
          title={t('auto.misc2.youAreOfflineHoursChecklistsAnd')}
        >
          <CloudOff className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t('auto.misc2.offline')}</span>
          {total > 0 && <span>· {total}</span>}
        </Badge>
        {dialog}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleSyncNow}
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2.5 py-0.5 text-xs font-medium hover:bg-amber-200 transition"
        data-testid="offline-sync-badge-pending"
        title={
          photos > 0
            ? `${pending} change(s) and ${photos} photo(s) queued — click to sync now`
            : `${pending} change(s) queued — click to sync now`
        }
      >
        {flushing ? (
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Cloud className="w-3.5 h-3.5" />
        )}
        <span>{total} pending</span>
      </button>
      {dialog}
    </>
  );
}
